import { inject, injectable, interfaces } from 'inversify';
import { ACDLError } from '../../../__legacy__/models/error.model';
import { IMainApiService, IPagedResult } from '../../../__legacy__/services/api.service';
import { IApiService } from '../../../__legacy__/services/api.service.base';
import { IApiServiceTraining } from '../../../__legacy__/services/api.service.training';
import { IValidJobType } from '../../jobs/jobs.model';
import { getJobApiPrefix } from '../../jobs/services/utils';

interface IGetHistoryDataRequest {
  pageNumber: number;
  pageSize: number;
  orderBy: string;
  orderType: string;
}

interface IHistoryDataDto {
  cost_calculated: number;
  date_created: string;
  job_id: string;
  job_type: 'Training' | 'Evaluation';
  user_id: string;
  workspace_id: string;
  job_name: string;
  is_deleted: boolean;
}

interface ICostCalculatedDto {
  workspace_id: string;
  job_id: string;
  job_type: 'Training' | 'Evaluation';
  cost_calculated: number;
  user_id: string;
  date_created: string;
}

interface IHistoryPointDto {
  x: number;
  y: number;
}

type IHistoryPlotDto = Record<string, IHistoryPointDto[]>;

export interface IHistoryDto {
  chart_data: IHistoryPlotDto;
  paged_data: IPagedResult<IHistoryDataDto>;
}

interface IUserDto {
  id: string;
  email: string;
}

export interface ICostUsageDto {
  usage: number;
  limit: number;
}

export interface ICloudCostsApiService {
  getHistoryDataAsync: (workspaceId: string, request: IGetHistoryDataRequest) => Promise<IHistoryDto | ACDLError>;
  getUserHistoryDataAsync: (workspaceId: string, userId: string, request: IGetHistoryDataRequest) => Promise<IHistoryDto | ACDLError>;
  getJobCalculatedCostAsync: (workspaceId: string, jobId: string, jobType: IValidJobType) => Promise<ICostCalculatedDto | ACDLError>;
  getUserEmailsAsync: (workspaceId: string) => Promise<IUserDto[] | ACDLError>;
  getCostUsageAsync: (workspaceId: string) => Promise<ICostUsageDto | ACDLError>;
}

export namespace ICloudCostsApiService {
  export const $: interfaces.ServiceIdentifier<ICloudCostsApiService> = Symbol('CLOUD_COSTS_API_SERVICE');
}

@injectable()
export class CloudCostsApiService implements ICloudCostsApiService {
  constructor(
    @inject(IApiServiceTraining.$) private readonly apiTrainingService: IApiService,
    @inject(IMainApiService.$) private readonly apiService: IApiService
  ) {}

  getHistoryDataAsync = async (workspaceId: string, request: IGetHistoryDataRequest): Promise<IHistoryDto | ACDLError> => {
    return await this.apiTrainingService.getAsync<IHistoryDto>(`/workspaces/${workspaceId}/cost-details`, { params: request });
  };

  getUserHistoryDataAsync = async (workspaceId: string, userId: string, request: IGetHistoryDataRequest): Promise<IHistoryDto | ACDLError> => {
    return await this.apiTrainingService.getAsync<IHistoryDto>(`/workspaces/${workspaceId}/cost-details/users/${userId}`, { params: request });
  };

  getJobCalculatedCostAsync = async (workspaceId: string, jobId: string, jobType: IValidJobType): Promise<ICostCalculatedDto | ACDLError> => {
    return this.apiTrainingService.getAsync<ICostCalculatedDto>(`/workspaces/${workspaceId}/cost-details/${getJobApiPrefix(jobType)}/${jobId}`);
  };

  getUserEmailsAsync = async (workspaceId: string): Promise<IUserDto[] | ACDLError> => {
    return await this.apiService.getAsync<IUserDto[]>(`/Models/GetUsersEmailsForCostsTable`, {
      params: {
        workspaceId,
      },
    });
  };

  getCostUsageAsync = async (workspaceId: string): Promise<ICostUsageDto | ACDLError> => {
    return await this.apiTrainingService.getAsync<ICostUsageDto>(`/workspaces/${workspaceId}/cost-usage`);
  };
}
